import { useEffect, useState } from "react";
import { IoChevronBackSharp, IoChevronForwardSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const LatestNews = ({ title = "Latest Buzz", className = "" }) => {
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const router = useNavigate();

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const apiKey = process.env.REACT_APP_API_KEY;
        if (!apiKey) return;

        const response = await fetch(
          `https://bmt-api.sicklecellfoundation.com/api/admin/get-events-and-news?page=${currentPage}`,
          {
            headers: {
              Authorization: `Bearer ${apiKey}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          setNewsData(result.data);
          setTotalPages(result.last_page);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [currentPage]);

  const handleReadMoreClick = (id) => {
    router(`/news/${id}`);
  };

  const generatePagination = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
      <button
        key={page}
        onClick={() => setCurrentPage(page)}
        className={`px-4 py-1 rounded-full text-sm transition-all ${
          currentPage === page
            ? "bg-primary_red text-white shadow-lg"
            : "bg-white/10 text-gray-300 hover:bg-white/20"
        }`}
      >
        {page}
      </button>
    ));
  };

  return (
    <section
      className={`relative w-full py-12 px-6 sm:px-16 overflow-hidden ${className}`}
    >
      {/* 🔥 DARK FUNKY BACKGROUND */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#0f0f1a] to-[#120c18]" />
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top,_rgba(255,0,80,0.15),_transparent_60%)]" />
      
      <div className="relative z-10">
        {/* ✅ FIXED TITLE COMPRESSION */}
        <div className="text-center mb-12">
          <h1 className="text-[42px] md:text-[56px] font-extrabold tracking-tight leading-tight bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-red-400 to-orange-400">
            {title}
          </h1>
          <p className="text-gray-400 mt-3 text-sm md:text-base">
            Culture. Community. Change.
          </p>
        </div>
        {/* ✅ SKELETON LOADERS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {newsData.map((news) => (
            <div
              key={news.id}
              onClick={() => handleReadMoreClick(news.id)}
              className="group cursor-pointer rounded-2xl overflow-hidden backdrop-blur-xl bg-white/10 border border-white/10 shadow-xl hover:shadow-pink-500/20 transition-all duration-300 hover:-translate-y-2"
            >
              {/* IMAGE */}
              <div className="relative h-[240px] overflow-hidden">
                <img
                  src={news.events_news_front_image}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                />

                {/* CATEGORY CHIP */}
                <span className="absolute top-4 left-4 bg-black/70 text-white text-xs px-3 py-1 rounded-full backdrop-blur-md">
                  {news.category_type}
                </span>

                {/* DATE FLOAT */}
                <div className="absolute bottom-4 right-4 bg-primary_red text-white px-3 py-2 rounded-xl text-xs font-bold">
                  {news.day || new Date().getDate()}{" "}
                  {news.month ||
                    new Date().toLocaleString("default", {
                      month: "short",
                    })}
                </div>
              </div>

              {/* CONTENT */}
              <div className="p-6 flex flex-col justify-between min-h-[220px]">
                <h2 className="font-bold text-lg leading-snug line-clamp-2 text-white mb-3">
                  {news.events_news_name}
                </h2>

                <p className="text-gray-400 text-sm line-clamp-3 mb-4">
                  {news.events_news_content}
                </p>

                <div className="flex justify-between items-center">
                  <span className="text-pink-400 font-semibold text-sm">
                    Read Story →
                  </span>

                  <span className="text-xs text-gray-500">
                    #{news.category_type?.toLowerCase()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* ✅ PAGINATION */}
        <div className="mt-16 flex items-center justify-center space-x-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            className="flex items-center gap-1 px-5 py-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
            disabled={currentPage === 1}
          >
            <IoChevronBackSharp /> Prev
          </button>

          <div className="flex gap-2">{generatePagination()}</div>

          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            className="flex items-center gap-1 px-5 py-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
            disabled={currentPage === totalPages}
          >
            Next <IoChevronForwardSharp />
          </button>
        </div>
      </div>
    </section>
  );
};

export default LatestNews;
